import { ref, computed, watch, nextTick } from 'vue'
import { getDomain } from 'tldts'

export const MAX_DOMAIN_ITEMS = 100

// DOM id of the domain-check results container. DomainCheckResults renders it;
// DomainCheckSearch scrolls to it when a same-query re-search skips navigation.
export const DOMAIN_CHECK_RESULTS_ID = 'domain-check-results'

// Unicode-aware regex for IDN support (e.g., münchen.de, 日本.jp)
export const DOMAIN_REGEX = /^(?:[\p{L}\p{N}](?:[\p{L}\p{N}-]*[\p{L}\p{N}])?\.)+[\p{L}]{2,}$/iu
export const SIMPLE_NAME_REGEX = /^[\p{L}\p{N}][\p{L}\p{N}.-]*$/iu

export function extractDomain(raw: string): string | null {
  let s = raw.trim().toLowerCase()
  if (!s) return null
  s = s.replace(/^https?:\/\//, '')
  s = s.replace(/^www\./, '')
  const slash = s.indexOf('/')
  if (slash !== -1) s = s.slice(0, slash)
  if (DOMAIN_REGEX.test(s)) return s
  const dom = getDomain(s)
  if (dom && DOMAIN_REGEX.test(dom)) return dom
  if (DOMAIN_REGEX.test(s)) return s
  return null
}

export function sanitizeDomainInput(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/.*$/, '')
    .replace(/[^\p{L}\p{N}.-]/gu, '')
}

function detectDelimiter(text: string): ',' | ';' | '\t' {
  const line = (text.split(/\r?\n/).find((l) => l.trim().length) || '').trim()
  const counts = { ',': 0, ';': 0, '\t': 0 } as Record<string, number>
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]!
    if (ch === '"') {
      if (line[i + 1] === '"') i++
      else inQuotes = !inQuotes
    } else if (!inQuotes) {
      if (ch === ',') counts[',']!++
      else if (ch === ';') counts[';']!++
      else if (ch === '\t') counts['\t']!++
    }
  }
  let best: ',' | ';' | '\t' = ','
  let bestCount = -1
  for (const d of [',', ';', '\t'] as const) {
    if (counts[d]! > bestCount) {
      best = d
      bestCount = counts[d]!
    }
  }
  return best
}

function splitCsvLine(line: string, delimiter: ',' | ';' | '\t'): string[] {
  const out: string[] = []
  let cur = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]!
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        cur += ch
      }
      continue
    }
    if (ch === '"') {
      inQuotes = true
      continue
    }
    if (ch === delimiter) {
      out.push(cur)
      cur = ''
    } else {
      cur += ch
    }
  }
  out.push(cur)
  return out.map((s) => s.trim())
}

export function parseCsvToDomainLines(text: string): string[] {
  const lines = text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .filter((l) => l.trim().length)
  if (!lines.length) return []
  const delimiter = detectDelimiter(text)

  const result: string[] = []
  for (const line of lines) {
    const fields = splitCsvLine(line, delimiter)
    for (const field of fields) {
      const dom = extractDomain(field)
      if (dom) result.push(dom)
    }
  }
  return result
}

export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result ?? ''))
      reader.onerror = () => reject(reader.error)
      reader.readAsText(file)
    } catch (e) {
      if (typeof (file as any).text === 'function') {
        ;(file as any)
          .text()
          .then((t: string) => resolve(t))
          .catch(reject)
      } else {
        reject(e)
      }
    }
  })
}

// Transfer codes typed into the search are credentials. They reach the results
// in memory, never in the URL, where browser history, server logs and
// analytics would keep them; a reload asks for them again.
const transferCodes = ref<Record<string, string>>({})

/**
 * The names of a search payload, each line cut to the leading run of name
 * characters after any scheme. An allowlist rather than a cut at the colon, so
 * a code after any other separator stays behind too.
 */
export function searchNames(payload: string): string {
  return payload
    .split('\n')
    .map((line) => line.trim().replace(/^https?:\/\//i, '').match(/^[\p{L}\p{N}.-]*/u)![0])
    .join('\n')
}

/** The names of a search payload that are valid domain names. */
export function validSearchNames(payload: string): string[] {
  return searchNames(payload)
    .split('\n')
    .filter((name) => DOMAIN_REGEX.test(name))
}

/** Keeps the transfer codes of a search payload in memory, as typed, and returns its lowercased names for the URL. */
export function takeTransferCodes(payload: string): string {
  // The scheme's colon is not the code separator.
  const lines = payload.split('\n').map((line) => line.replace(/^\s*https?:\/\//i, ''))
  const codes: Record<string, string> = {}
  for (const line of lines) {
    const i = line.indexOf(':')
    const code = i < 0 ? '' : line.slice(i + 1).trim()
    if (!code) continue
    // Keyed as the search page normalises the name, or "www." would lose it.
    const name = line.slice(0, i).replace(/\s/g, '')
    codes[extractDomain(name) ?? name.toLowerCase()] = code
  }
  transferCodes.value = codes
  return searchNames(payload).toLowerCase()
}

/** Forgets the codes of the last search, for when someone else may be at the browser. */
export function forgetTransferCodes(): void {
  transferCodes.value = {}
}

/** The transfer code given with the last search for a domain. */
export function searchTransferCode(domain: string): string | undefined {
  return transferCodes.value[domain]
}

export function useDomainSearch() {
  const searchInput = ref('')
  const multiDomain = ref(false)
  const rawTokens = ref<string[]>([])
  const parsedList = ref<string[]>([])
  const invalidList = ref<string[]>([])
  const dedupRemoved = ref(0)
  const truncated = ref(false)

  const isValid = computed(() => {
    if (multiDomain.value) return parsedList.value.length > 0
    const s = searchInput.value.trim().toLowerCase()
    if (s.length === 0) return true
    return SIMPLE_NAME_REGEX.test(s)
  })

  function parseList(input: string) {
    const tokens = input
      .replace(/\r\n?/g, '\n')
      .split(/[\n,;]+/)
      .map((tok) => tok.trim())
      .filter(Boolean)

    const cleaned: string[] = []
    const invalid: string[] = []
    const seen = new Map<string, { hasAuth: boolean; index: number }>()

    for (const tok of tokens) {
      const i = tok.indexOf(':')
      let out: string | null
      if (i > -1) {
        const dom = extractDomain(tok.slice(0, i))
        const auth = tok.slice(i + 1).trim()
        out = dom ? `${dom}${auth ? ':' + auth : ''}` : null
      } else {
        out = extractDomain(tok)
      }

      if (!out) {
        invalid.push(tok)
        continue
      }

      const [domain, auth] = out.split(':', 2)
      const prev = seen.get(domain ?? '')
      if (!prev && domain) {
        seen.set(domain, { hasAuth: !!auth, index: cleaned.length })
        cleaned.push(out)
      } else if (auth && prev && !prev.hasAuth && domain) {
        cleaned[prev.index] = out
        seen.set(domain, { hasAuth: true, index: prev.index })
      }
    }

    const was = cleaned.length
    const finalList = cleaned.slice(0, MAX_DOMAIN_ITEMS)
    dedupRemoved.value = tokens.length - was
    truncated.value = cleaned.length > MAX_DOMAIN_ITEMS

    rawTokens.value = tokens
    parsedList.value = finalList
    invalidList.value = invalid
  }

  function toggleMultiDomain() {
    multiDomain.value = !multiDomain.value
    searchInput.value = ''
    rawTokens.value = []
    parsedList.value = []
    invalidList.value = []
    dedupRemoved.value = 0
    truncated.value = false
  }

  function applyCleanup() {
    if (!multiDomain.value) return
    searchInput.value = parsedList.value.join('\n')
  }

  function clearAll() {
    searchInput.value = ''
  }

  function getSearchPayload(): string {
    return multiDomain.value
      ? parsedList.value.join('\n')
      : sanitizeDomainInput(searchInput.value)
  }

  async function handleFile(files: File[] | undefined) {
    if (!files || !files.length) return
    try {
      const file = files[0]!
      const text = await readFileAsText(file)
      const lower = file.name.toLowerCase()

      multiDomain.value = true
      await nextTick()

      if (lower.endsWith('.csv')) {
        const lines = parseCsvToDomainLines(text)
        searchInput.value = lines.join('\n')
      } else {
        searchInput.value = text
      }
    } catch (e) {
      console.error('Error handling file:', e)
    }
  }

  watch(
    searchInput,
    (val) => {
      if (multiDomain.value) parseList(val)
    },
    { immediate: true }
  )

  return {
    searchInput,
    multiDomain,
    rawTokens,
    parsedList,
    invalidList,
    dedupRemoved,
    truncated,
    isValid,
    toggleMultiDomain,
    applyCleanup,
    clearAll,
    getSearchPayload,
    handleFile,
    MAX_ITEMS: MAX_DOMAIN_ITEMS
  }
}
